import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle, Info, PenLine } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea, TextInput } from '../ui/Form';

/**
 * Promise-based replacements for window.alert / window.confirm / window.prompt.
 *
 *   const { confirm, prompt, alert } = useDialog();
 *   if (!(await confirm({ message: 'Delete this?', danger: true }))) return;
 *   const reason = await prompt({ message: 'Reason?', required: true });   // string | null
 *   await alert({ message: 'Copied' });
 *
 * confirm resolves true/false, prompt resolves the text or null when cancelled,
 * alert resolves when dismissed. Concurrent calls are queued and shown one at a time.
 */

const DialogContext = createContext(null);

const missingProvider = (fallback) => async () => {
    console.error('useDialog was called outside of <DialogProvider>');
    return fallback;
};

const NO_PROVIDER = {
    confirm: missingProvider(false),
    prompt: missingProvider(null),
    alert: missingProvider(undefined),
};

export const useDialog = () => useContext(DialogContext) || NO_PROVIDER;

export const DialogProvider = ({ children }) => {
    const queueRef = useRef([]);
    // Mirrors `active` so queue decisions never race React state batching
    const activeRef = useRef(null);
    const [active, setActive] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [inputError, setInputError] = useState('');

    const show = useCallback((request) => {
        activeRef.current = request;
        setInputValue(request?.defaultValue || '');
        setInputError('');
        setActive(request);
    }, []);

    // Show the next queued dialog, but only if nothing is on screen
    const showNext = useCallback(() => {
        if (activeRef.current) return;
        const next = queueRef.current.shift();
        if (next) show(next);
    }, [show]);

    const enqueue = useCallback(
        (request) =>
            new Promise((resolve) => {
                queueRef.current.push({ ...request, resolve });
                showNext();
            }),
        [showNext]
    );

    const api = useMemo(
        () => ({
            confirm: (options = {}) => enqueue({ ...options, type: 'confirm' }),
            prompt: (options = {}) => enqueue({ ...options, type: 'prompt' }),
            alert: (options = {}) => enqueue({ ...options, type: 'alert' }),
        }),
        [enqueue]
    );

    const finish = (result) => {
        const current = activeRef.current;
        activeRef.current = null;
        setActive(null);
        current?.resolve(result);
        // Let the closing transition finish before the next queued dialog appears.
        setTimeout(showNext, 150);
    };

    const handleCancel = () => {
        if (!active) return;
        if (active.type === 'confirm') finish(false);
        else if (active.type === 'prompt') finish(null);
        else finish(undefined);
    };

    const handleConfirm = () => {
        if (active.type === 'prompt') {
            const value = inputValue.trim();
            if (active.required && !value) {
                setInputError(active.requiredMessage || 'This field is required');
                return;
            }
            finish(value);
        } else if (active.type === 'confirm') {
            finish(true);
        } else {
            finish(undefined);
        }
    };

    const isAlert = active?.type === 'alert';
    const isPrompt = active?.type === 'prompt';
    const defaultTitle = { alert: 'Notice', confirm: 'Please confirm', prompt: 'Enter details' }[active?.type];
    const icon = active?.danger ? AlertTriangle : isPrompt ? PenLine : isAlert ? Info : HelpCircle;
    const tone = active?.danger ? 'danger' : isAlert ? 'info' : 'gold';

    return (
        <DialogContext.Provider value={api}>
            {children}
            <Modal
                open={Boolean(active)}
                onClose={handleCancel}
                size="sm"
                icon={icon}
                tone={tone}
                title={active ? active.title || defaultTitle : ''}
                footer={
                    active && (
                        <>
                            {!isAlert && (
                                <Button variant="ghost" onClick={handleCancel}>
                                    {active.cancelText || 'Cancel'}
                                </Button>
                            )}
                            <Button
                                variant={active.danger ? 'danger' : 'primary'}
                                onClick={handleConfirm}
                                data-autofocus={!isPrompt ? true : undefined}
                            >
                                {active.confirmText || (isAlert ? 'OK' : 'Confirm')}
                            </Button>
                        </>
                    )
                }
            >
                {active && (
                    <div className="flex flex-col gap-3">
                        {active.message && (
                            <p className="text-sm text-cm-muted whitespace-pre-line m-0">{active.message}</p>
                        )}
                        {isPrompt &&
                            (active.multiline ? (
                                <Textarea
                                    data-autofocus
                                    label={active.label || undefined}
                                    placeholder={active.placeholder || ''}
                                    rows={3}
                                    value={inputValue}
                                    error={inputError || undefined}
                                    onChange={(e) => {
                                        setInputValue(e.target.value);
                                        if (inputError) setInputError('');
                                    }}
                                />
                            ) : (
                                <TextInput
                                    data-autofocus
                                    label={active.label || undefined}
                                    placeholder={active.placeholder || ''}
                                    value={inputValue}
                                    error={inputError || undefined}
                                    onChange={(e) => {
                                        setInputValue(e.target.value);
                                        if (inputError) setInputError('');
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleConfirm();
                                        }
                                    }}
                                />
                            ))}
                    </div>
                )}
            </Modal>
        </DialogContext.Provider>
    );
};

export default DialogProvider;
